import { useContext, useState } from "react";
import { useMutation } from "@apollo/client";
import ShipmentInvoiceRow from "./ShipmentInvoiceRow";
import InvoiceRow from "./InvoiceRow";
import ProductSelector from "../ProductSelector";
import SpecialItemForm from "./SpecialItemForm";
import { Alert } from "components/base/Toast";
import { CREATE_SHIPMENT_INVOICE_ROW } from "./graphql";
import ShipmentContext from "../ShipmentContext";
import { createShipmentInvoicePoGroups } from "../../utils";
import Button from "components/base/Button";
import { CheckBox } from "components/base/base";
import { useModals } from "ModalProvider";

function ShipmentInvoiceGroup({
  shipmentInvoice,
  group,
  onSelectRow,
  selectedRows,
  onSelectAll,
  selectAll,
  onCopyEan,
  onCopyUpc,
}) {
  const { state, dispatch } = useContext(ShipmentContext);
  const [createShipmentInvoiceRow, createShipmentInvoiceRowRes] = useMutation(
    CREATE_SHIPMENT_INVOICE_ROW,
  );
  const modal = useModals();
  const hasRows = group.shipmentInvoiceRows.length > 0;

  function createRow({ productId, specialRowInput }, onCompleted) {
    createShipmentInvoiceRow({
      variables: {
        shipmentInvoiceId: shipmentInvoice.id,
        productId,
        poId: group.po ? group.po.id : null,
        specialRowInput,
      },
      onCompleted: (data) => {
        Alert("success", "Product added to invoice");
        dispatch({
          type: "createShipmentInvoiceRow",
          payload: {
            ...data.createShipmentInvoiceRow,
            shipmentInvoiceId: shipmentInvoice.id,
          },
        });
        onCompleted?.();
      },
      onError: (error) => Alert("error", error.message),
    });
  }

  function tryAddProduct() {
    modal.present({
      title: "Add Product",
      maxWidth: "max-w-3xl",
      children: (
        <ProductSelector
          onSelectProduct={(product) => {
            modal.hide();
            createRow({ productId: product.id });
          }}
        />
      ),
    });
  }

  function tryAddSpecialItem() {
    modal.present({
      title: "Create Special Item",
      maxWidth: "max-w-7xl",
      children: (
        <SpecialItemForm
          onSaveSpecialItem={(specialRowInput) =>
            createRow({ specialRowInput }, modal.hide)
          }
        />
      ),
    });
  }

  return (
    <div>
      {group.shipmentInvoiceRows.map((row) => (
        <ShipmentInvoiceRow
          key={row.id}
          shipmentInvoiceId={shipmentInvoice.id}
          row={row}
          archived={!state.isEffective}
          selectionControl={
            <CheckBox
              checked={selectedRows.some((item) => item.id === row.id)}
              onChange={(checked) => onSelectRow(row, checked)}
            />
          }
        />
      ))}

      <div className="flex flex-wrap items-center justify-between gap-3 bg-white px-6 py-4 dark:bg-gray-900">
        <div className="flex items-center gap-2">
          <Button
            onClick={tryAddProduct}
            loading={createShipmentInvoiceRowRes.loading}
            className="rounded-md px-2 py-1 text-xs transition-colors hover:bg-sky-100 active:bg-sky-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 dark:hover:bg-sky-900/60 dark:active:bg-sky-800/70"
          >
            + Product
          </Button>
          <Button
            onClick={tryAddSpecialItem}
            className="rounded-md px-2 py-1 text-xs transition-colors hover:bg-sky-100 active:bg-sky-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 dark:hover:bg-sky-900/60 dark:active:bg-sky-800/70"
          >
            + Special Item
          </Button>
        </div>
        {hasRows && (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={onSelectAll}
              className="rounded-md px-2 py-1 text-xs transition-colors hover:bg-sky-100 active:bg-sky-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 dark:hover:bg-sky-900/60 dark:active:bg-sky-800/70"
            >
              {selectAll ? "Deselect All" : "Select All"}
            </Button>
            {selectedRows.length > 0 && (
              <>
                <Button
                  onClick={onCopyEan}
                  className="rounded-md px-2 py-1 text-xs transition-colors hover:bg-sky-100 active:bg-sky-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 dark:hover:bg-sky-900/60 dark:active:bg-sky-800/70"
                >
                  Copy EAN ({selectedRows.length})
                </Button>
                <Button
                  onClick={onCopyUpc}
                  className="rounded-md px-2 py-1 text-xs transition-colors hover:bg-sky-100 active:bg-sky-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 dark:hover:bg-sky-900/60 dark:active:bg-sky-800/70"
                >
                  Copy UPC ({selectedRows.length})
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ShipmentInvoice({ shipmentInvoice }) {
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const shipmentInvoicePoGroups =
    createShipmentInvoicePoGroups(shipmentInvoice);

  // Get all rows in the current invoice
  const getAllRows = () => {
    let allRows = [];
    shipmentInvoicePoGroups.forEach((group) => {
      group.shipmentInvoiceRows.forEach((row) => {
        allRows.push({
          id: row.id,
          productName: row.productName,
          productNumber: row.itemNumber,
          qty: row.qty,
          innerBoxBarcodeEan: row.product?.innerBoxBarcodeEan || "",
          innerBoxBarcodeUpc: row.product?.innerBoxBarcodeUpc || "",
        });
      });
    });
    return allRows;
  };

  // Handle row selection
  const handleRowSelection = (row, isSelected) => {
    if (isSelected) {
      setSelectedRows((prev) => [
        ...prev,
        {
          id: row.id,
          productName: row.productName,
          productNumber: row.itemNumber,
          qty: row.qty,
          innerBoxBarcodeEan: row.product?.innerBoxBarcodeEan || "",
          innerBoxBarcodeUpc: row.product?.innerBoxBarcodeUpc || "",
        },
      ]);
    } else {
      setSelectedRows((prev) => prev.filter((item) => item.id !== row.id));
    }
  };

  // Toggle select all/deselect all
  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedRows([]);
    } else {
      setSelectedRows(getAllRows());
    }
    setSelectAll(!selectAll);
  };

  async function copyAsExcelTable(headers, data, poNumbers) {
    const poLine =
      poNumbers && poNumbers.length > 0
        ? `客户PO号: ${poNumbers.join(",")}\n`
        : "";

    const htmlTable =
      `<div>${poLine.replace(/\n/g, "<br/>")}</div>` +
      `<table><thead><tr>` +
      headers.map((h) => `<th>${h.label}</th>`).join("") +
      `</tr></thead><tbody>` +
      data
        .map(
          (row) =>
            `<tr>` +
            headers.map((h) => `<td>${row[h.key] ?? ""}</td>`).join("") +
            `</tr>`,
        )
        .join("") +
      `</tbody></table>`;

    // Construct TSV
    const tsvRows = [
      poLine.trim(),
      headers.map((h) => h.label).join("\t"),
      ...data.map((row) =>
        headers
          .map((h) =>
            String(row[h.key] ?? "")
              .replace(/\t/g, " ")
              .replace(/\r?\n/g, " "),
          )
          .join("\t"),
      ),
    ].filter(Boolean);
    const tsvContent = tsvRows.join("\n");

    // try to write HTML table to clipboard (supported by some browsers)
    if (navigator.clipboard && window.ClipboardItem) {
      try {
        await navigator.clipboard.write([
          new window.ClipboardItem({
            "text/html": new Blob([htmlTable], { type: "text/html" }),
            "text/plain": new Blob([tsvContent], { type: "text/plain" }),
          }),
        ]);

        return true;
      } catch (e) {
        return false;
      }
    }

    // Fallback: only write TSV
    try {
      await navigator.clipboard.writeText(tsvContent);
      return true;
    } catch (e) {
      return false;
    }
  }

  // Copy selected products information
  const copySelectedProducts = async (barcodeType) => {
    if (selectedRows.length === 0) {
      Alert("warning", "Please select products first");
      return;
    }
    const dataWithBarcode = selectedRows.map((row) => ({
      productNumber: row.productNumber,
      productName: row.productName,
      qty: row.qty,
      barCode:
        barcodeType === "upc" ? row.innerBoxBarcodeUpc : row.innerBoxBarcodeEan,
    }));
    const headers = [
      { key: "productNumber", label: "型号" },
      { key: "productName", label: "货名" },
      { key: "qty", label: "数量" },
      { key: "barCode", label: "条码" },
    ];
    const poNumbers = shipmentInvoice.pos
      ? shipmentInvoice.pos.map((i) => i.number)
      : [];
    const ok = await copyAsExcelTable(headers, dataWithBarcode, poNumbers);
    if (ok) {
      Alert("success", `Copied!`);
    } else {
      Alert("error", "Copy failed, please check permissions.");
    }
  };

  return (
    <div className="mt-6 overflow-hidden rounded-xl bg-white dark:bg-gray-900 dark:text-gray-300">
      <InvoiceRow shipmentInvoice={shipmentInvoice} />
      {shipmentInvoicePoGroups.map((group, groupIndex) => (
        <ShipmentInvoiceGroup
          shipmentInvoice={shipmentInvoice}
          group={group}
          key={groupIndex}
          onSelectRow={handleRowSelection}
          selectedRows={selectedRows}
          onSelectAll={toggleSelectAll}
          selectAll={selectAll}
          onCopyEan={() => copySelectedProducts("ean")}
          onCopyUpc={() => copySelectedProducts("upc")}
        />
      ))}
    </div>
  );
}

export default ShipmentInvoice;
